import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/ui/button';
import { Input, Field } from '@/ui/input';
import { MicroLabel } from '@/ui/micro-label';
import { ErrorState } from '@/ui/error-state';
import { Wordmark } from '@/domain/wordmark';

/**
 * Where a vendor waits when this deployment requires onboarding approval before dataset routes
 * open (`ONBOARDING_REQUIRED`; the guard sends them here, and the API refuses those routes with
 * `onboarding_required` until an operator approves).
 *
 * It shows the application's real state and nothing more. In particular it does **not** offer a
 * document upload: `POST /verification/documents` takes a caller-supplied S3 key rather than
 * accepting a file, so a picker here would be a control that cannot do what it appears to.
 * Categories, resubmission and the changes-requested loop are described in pipelines.md section 1
 * and are not built.
 */

/**
 * The row has no `status` column -- it has `submitted_at`, `outcome` and `note` -- so the stage is
 * derived from which of those are present. `documents_required` is the shape the API substitutes
 * when there is no application at all (admin.controller.ts).
 */
type Application = {
  status?: string;
  submitted_at?: string | null;
  outcome?: string | null;
  note?: string | null;
  organization_snapshot?: { name?: string } | null;
};

function stage(application: Application | null) {
  if (!application || application.status === 'documents_required' || !application.submitted_at) return 'not_submitted';
  if (!application.outcome) return 'under_review';
  return application.outcome;
}

const COPY: Record<string, { title: string; body: string }> = {
  not_submitted: {
    title: 'Tell us who you are',
    body: 'Your organization has to be verified before dataset submission opens. Start by confirming its legal name.'
  },
  under_review: {
    title: 'Verification in review',
    body: 'Your application has been submitted. An operator reviews it and you will be let through here once it is approved.'
  },
  approved: {
    title: 'Verification approved',
    body: 'Your organization is verified. Reload to continue into the portal.'
  },
  rejected: {
    title: 'Verification was not approved',
    body: 'An operator declined this application. Their note is below.'
  },
  changes_requested: {
    title: 'Changes requested',
    body: 'An operator asked for something to be corrected before this can be approved. Their note is below.'
  }
};

export function VendorOnboarding() {
  const queryClient = useQueryClient();
  const [organization, setOrganization] = useState('');
  const [error, setError] = useState('');

  const state = useQuery({
    queryKey: ['vendor', 'verification'],
    queryFn: () => api<Application>('/verification/state', { realm: 'vendor' }),
    // An operator's decision happens elsewhere and pushes nothing to this screen, so this is the
    // only thing that will notice it.
    refetchInterval: 15000
  });

  const submit = useMutation({
    mutationFn: () =>
      api('/verification/submit', {
        method: 'POST',
        realm: 'vendor',
        body: JSON.stringify({ organization: { name: organization } })
      }),
    onSuccess: () => {
      setError('');
      void queryClient.invalidateQueries({ queryKey: ['vendor', 'verification'] });
    },
    onError: cause => setError(cause instanceof ApiError ? cause.message : 'Could not submit.')
  });

  const at = stage(state.data ?? null);
  const copy = COPY[at] ?? { title: 'Verification', body: `This application is ${at}.` };

  return (
    <div className="grid min-h-screen place-items-center bg-background px-4 py-12">
      <div className="w-full max-w-[420px]">
        <div className="grid justify-items-center gap-3">
          <Wordmark height={16} />
          <span className="rounded-[3px] border border-border px-2 py-1 font-mono text-[10px] tracking-[0.12em] text-muted uppercase">
            Vendor portal
          </span>
        </div>

        {state.isPending ? (
          <p className="mt-8 text-center text-[13px] text-muted">Loading your application…</p>
        ) : state.isError ? (
          <ErrorState
            className="mt-8"
            error={state.error}
            title="Could not load your application"
            onRetry={() => void state.refetch()}
          />
        ) : (
          <div className="mt-8 grid gap-5 rounded border border-border bg-card px-8 py-7">
            <div className="grid gap-1.5">
              <div className="flex items-center gap-2 text-muted">
                <ShieldCheck size={16} aria-hidden />
              </div>
              <h1 className="text-[22px] font-medium text-foreground">{copy.title}</h1>
              <p className="text-[13px] leading-relaxed text-muted">{copy.body}</p>
            </div>

            {state.data?.note ? (
              <p className="rounded border border-border bg-inset px-3 py-2 text-[13px] leading-relaxed text-foreground">
                {state.data.note}
              </p>
            ) : null}

            {at === 'not_submitted' || at === 'changes_requested' ? (
              <form
                onSubmit={event => {
                  event.preventDefault();
                  submit.mutate();
                }}
                className="grid gap-4"
              >
                <Field label="Registered organization name" htmlFor="organization">
                  <Input
                    id="organization"
                    value={organization}
                    onChange={event => setOrganization(event.target.value)}
                    placeholder="Third Origin Robotics Ltd"
                    required
                  />
                </Field>
                {error ? <p className="text-[13px] text-danger">{error}</p> : null}
                <Button type="submit" disabled={submit.isPending} className="w-full">
                  {submit.isPending ? 'Submitting…' : 'Submit for verification'}
                </Button>
              </form>
            ) : (
              <MicroLabel size="xs">
                {at === 'under_review' ? 'Checked every 15 seconds' : `Application ${at}`}
              </MicroLabel>
            )}
          </div>
        )}

        <MicroLabel size="xs" caps={false} className="mt-6 block text-center">
          miraxis.ai · vendor support: partners@miraxis.ai
        </MicroLabel>
      </div>
    </div>
  );
}
